/**
 * ReportsPage - Unified Reports View
 *
 * Combines filter controls, statistics, and detection table in a single cohesive page.
 * Features:
 * - Date and room selection filters
 * - Real-time stats bar
 * - Expandable room > section grouped detection table
 * - Full PDF and Excel export
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Table, Target, FlaskConical, FolderOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCategoryContext } from "@/contexts/CategoryContext";
import { apiService } from "@/services/api";
import { Session, Room, HistoryItem } from "@/types";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import ReportFilters from "@/components/reports/ReportFilters";
import ReportStatsBar from "@/components/reports/ReportStatsBar";
import DetectionsTable, {
  RoomGroup,
  SectionGroup,
  DetectionRow,
} from "@/components/reports/DetectionsTable";
import { formatClassNameForDisplay } from "@/utils/classNameUtils";
import { getClassColor } from "@/utils/colorUtils";

interface ReportStats {
  totalAnalyses: number;
  totalDetections: number;
  totalUserDrawn: number;
  totalCorrected: number;
  avgProcessingTime: number;
  totalSessions: number;
  confidenceStats: {
    certain: number;
    uncertain: number;
  };
}

interface SectionInfo {
  id: number;
  name: string;
}

type ReportMode = "section-based" | "testing";

const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const { getClassCategory } = useCategoryContext();
  const [searchParams] = useSearchParams();

  // Report mode toggle (admin only)
  const [reportMode, setReportMode] = useState<ReportMode>("section-based");

  // Track if we've applied URL params (to prevent date-change effect from overwriting)
  const urlParamsApplied = useRef(false);
  const urlParamsRoomsApplied = useRef(false); // Track if rooms came from URL params

  // Filter state
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [sessionsWithDates, setSessionsWithDates] = useState<
    Map<string, Session[]>
  >(new Map());
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [selectedRooms, setSelectedRooms] = useState<Set<number>>(new Set());

  // Testing sessions state
  const [testingSessions, setTestingSessions] = useState<Session[]>([]);
  const [selectedTestingSession, setSelectedTestingSession] = useState<Session | null>(null);

  // Section info cache
  const [sectionCache, setSectionCache] = useState<Map<number, SectionInfo>>(
    new Map(),
  );

  // Stats state
  const [reportStats, setReportStats] = useState<ReportStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Table state
  const [roomGroups, setRoomGroups] = useState<RoomGroup[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);

  // Initial loading
  const [loading, setLoading] = useState(true);

  // Export state
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Details toggle for table (only admins can see individual detections)
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedRooms.size > 0) {
      fetchReportData();
    }
  }, [selectedDate, selectedRooms]);

  // Apply URL params or auto-select most recent day with sessions
  useEffect(() => {
    if (
      sessionsWithDates.size > 0 &&
      allRooms.length > 0 &&
      !urlParamsApplied.current
    ) {
      urlParamsApplied.current = true;

      // Check for URL params
      const dateParam = searchParams.get("date");
      const roomsParam = searchParams.get("rooms");

      console.log("ReportsPage - URL params:", { dateParam, roomsParam });
      console.log(
        "ReportsPage - all rooms:",
        allRooms.map((r) => ({ id: r.id, name: r.name })),
      );

      if (dateParam) {
        // Parse date param (YYYY-MM-DD format)
        const parsedDate = new Date(dateParam + "T00:00:00");
        if (!isNaN(parsedDate.getTime()) && isDateClickable(parsedDate)) {
          setSelectedDate(parsedDate);
          setCurrentMonth(
            new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1),
          );

          // Update available rooms for this date
          const dateKey = parsedDate.toDateString();
          const sessionsOnDate = sessionsWithDates.get(dateKey) || [];
          const filteredSessions = sessionsOnDate.filter((session) => {
            const userMatch = isAdmin || session.user_id === user?.id;
            const statusMatch =
              session.status === "completed" || !session.is_active;
            return userMatch && statusMatch;
          });
          const roomIds = new Set(
            filteredSessions.map((s) => s.room_id).filter(Boolean),
          );
          const available = allRooms.filter((room) => roomIds.has(room.id));
          setAvailableRooms(available);

          // Apply rooms param if provided
          if (roomsParam) {
            const roomIdList = roomsParam
              .split(",")
              .map((id) => parseInt(id, 10))
              .filter((id) => !isNaN(id));
            // Select rooms that exist (regardless of sessions on this date - the URL knows what was selected)
            const validRoomIds = roomIdList.filter((id) =>
              allRooms.some((r) => r.id === id),
            );
            console.log("ReportsPage - roomIdList from param:", roomIdList);
            console.log(
              "ReportsPage - allRooms ids:",
              allRooms.map((r) => r.id),
            );
            console.log("ReportsPage - validRoomIds:", validRoomIds);
            if (validRoomIds.length > 0) {
              setSelectedRooms(new Set(validRoomIds));
              urlParamsRoomsApplied.current = true; // Mark that rooms came from URL
            } else if (available.length > 0) {
              // Fallback to first available room
              setSelectedRooms(new Set([available[0].id]));
            }
          } else if (available.length > 0) {
            // No rooms param, select all available
            setSelectedRooms(new Set(available.map((r) => r.id)));
          }
          return;
        }
      }

      // No valid URL params - fall back to most recent date
      const mostRecentDate = findMostRecentDateWithSessions();
      if (mostRecentDate) {
        setSelectedDate(mostRecentDate);
        updateAvailableRooms(mostRecentDate);
      }
    }
  }, [sessionsWithDates, allRooms, user?.id, isAdmin, searchParams]);

  // Update available rooms when selected date changes (skip if rooms came from URL params)
  useEffect(() => {
    // Don't override room selection if it came from URL params
    if (urlParamsRoomsApplied.current) {
      return;
    }
    if (selectedDate && isDateClickable(selectedDate)) {
      updateAvailableRooms(selectedDate);
    }
  }, [selectedDate, sessionsWithDates]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      const [sessionsResponse, roomsResponse, testingResponse] = await Promise.all([
        apiService.getSessions(1000, 0),
        apiService.getRooms(),
        isAdmin ? apiService.getTestingSessions() : Promise.resolve({ sessions: [] }),
      ]);

      const sessions = sessionsResponse.sessions as Session[];
      const rooms = roomsResponse.rooms as Room[];

      // Group sessions by date
      const sessionsByDate = new Map<string, Session[]>();
      sessions.forEach((session) => {
        const dateKey = new Date(session.created_at).toDateString();
        if (!sessionsByDate.has(dateKey)) {
          sessionsByDate.set(dateKey, []);
        }
        sessionsByDate.get(dateKey)!.push(session);
      });

      setSessionsWithDates(sessionsByDate);
      setAllRooms(rooms);

      // Set testing sessions (for admins)
      if (isAdmin && testingResponse?.sessions) {
        setTestingSessions(testingResponse.sessions);
      }
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = useCallback(async () => {
    if (!selectedDate || selectedRooms.size === 0) return;

    try {
      setLoadingStats(true);
      setLoadingTable(true);

      const dateKey = selectedDate.toDateString();
      const sessionsOnDate = sessionsWithDates.get(dateKey) || [];

      // Filter sessions
      const filteredSessions = sessionsOnDate.filter((session) => {
        const userMatch = isAdmin || session.user_id === user?.id;
        const roomMatch =
          !session.room_id || selectedRooms.has(session.room_id);
        const statusMatch =
          session.status === "completed" || !session.is_active;
        return userMatch && roomMatch && statusMatch;
      });

      let totalAnalyses = 0;
      let totalDetections = 0;
      let totalUserDrawn = 0;
      let totalCorrected = 0;
      let totalProcessingTime = 0;
      let processingTimes: number[] = [];
      let certainDetections = 0;
      let uncertainDetections = 0;

      // Build room > section > detections structure
      const roomSectionMap = new Map<
        number,
        Map<number | null, DetectionRow[]>
      >();
      const newSectionCache = new Map(sectionCache);

      for (const session of filteredSessions) {
        if (!session.room_id) continue;

        try {
          const [analysesResponse, correctionsResponse, sectionsData] =
            await Promise.all([
              apiService.getSessionAnalyses(session.id, 1000, 0),
              apiService
                .getUserCorrections(session.id)
                .catch(() => ({ corrections: [] })),
              apiService
                .getRoomSections(session.room_id)
                .catch(() => [] as any[]),
            ]);

          const analyses = analysesResponse.analyses || [];
          const corrections = correctionsResponse.corrections || [];
          const sections = sectionsData || [];

          // Cache section info
          sections.forEach((section: any) => {
            if (section && section.id && !newSectionCache.has(section.id)) {
              newSectionCache.set(section.id, {
                id: section.id,
                name: section.name || `Section ${section.id}`,
              });
            }
          });

          // Create corrections map
          const correctionsMap = new Map<string, any>();
          corrections.forEach((correction: any) => {
            const key = `${correction.image_key}-${correction.detection_id}`;
            correctionsMap.set(key, correction);
          });

          totalAnalyses += analyses.length;

          analyses.forEach((analysis: HistoryItem) => {
            totalDetections += analysis.total_detections;
            processingTimes.push(analysis.processing_time);
            totalProcessingTime += analysis.processing_time;

            const sectionId = analysis.section_id ?? null;
            const sectionInfo = sectionId
              ? newSectionCache.get(sectionId)
              : null;

            if (analysis.detections) {
              analysis.detections.forEach((detection: any, index: number) => {
                const imageKey = `analysis-${analysis.id}`;
                const correctionKey = `${imageKey}-${detection.id}`;
                const correction = correctionsMap.get(correctionKey);

                // Check for user correction - from corrections map, explicit flag, or presence of corrected class
                const isUserCorrected = !!(correction || detection.is_user_corrected || detection.user_corrected_class);
                const userCorrectedClass = correction?.corrected_class_name || detection.user_corrected_class;

                if (detection.user_drawn) {
                  totalUserDrawn++;
                }
                if (isUserCorrected) {
                  totalCorrected++;
                }

                if (detection.is_confident) {
                  certainDetections++;
                } else {
                  uncertainDetections++;
                }

                // Build detection row
                const room = allRooms.find((r) => r.id === session.room_id);
                if (!room) return;

                if (!roomSectionMap.has(room.id)) {
                  roomSectionMap.set(room.id, new Map());
                }

                const sectionMap = roomSectionMap.get(room.id)!;
                if (!sectionMap.has(sectionId)) {
                  sectionMap.set(sectionId, []);
                }

                const topClass =
                  userCorrectedClass ||
                  detection.top_classes?.[0]?.label ||
                  detection.original_top1_class ||
                  "Unknown";
                const classificationConfidence =
                  detection.top_classes?.[0]?.score ||
                  detection.confidence ||
                  0;

                sectionMap.get(sectionId)!.push({
                  id: detection.id,
                  sessionId: session.id,
                  analysisId: analysis.id,
                  roomName: room.name,
                  sectionId,
                  sectionName:
                    sectionInfo?.name ||
                    (sectionId ? `Section ${sectionId}` : null),
                  imageName: analysis.image_name,
                  bbox: detection.bbox,
                  topClass,
                  confidence: detection.confidence || 0,
                  classificationConfidence,
                  qualityScore: detection.quality_score,
                  isUserCorrected,
                  userCorrectedClass,
                  originalTopClass: detection.original_top1_class,
                  isUserDrawn: !!detection.user_drawn,
                  timestamp: analysis.timestamp,
                  uniqueId: `${analysis.id}-${detection.id}`,
                  orderNumber: 0,
                  hasUncertainDetections: !detection.is_confident,
                });
              });
            }
          });
        } catch (error) {
          console.error(
            `Failed to fetch analyses for session ${session.id}:`,
            error,
          );
        }
      }

      setSectionCache(newSectionCache);

      // Build room groups with sections
      const groups: RoomGroup[] = [];
      roomSectionMap.forEach((sectionMap, roomId) => {
        const room = allRooms.find((r) => r.id === roomId);
        if (!room) return;

        const sections: SectionGroup[] = [];
        sectionMap.forEach((detections, sectionId) => {
          // Sort detections by timestamp and assign order numbers
          detections.sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          );
          detections.forEach((d, i) => (d.orderNumber = i + 1));

          const sectionInfo = sectionId ? newSectionCache.get(sectionId) : null;

          sections.push({
            sectionId,
            sectionName:
              sectionInfo?.name || (sectionId ? `Section ${sectionId}` : null),
            detections,
            isExpanded: true,
          });
        });

        // Sort sections: named sections first, then unassigned
        sections.sort((a, b) => {
          if (a.sectionId === null) return 1;
          if (b.sectionId === null) return -1;
          return (a.sectionName || "").localeCompare(b.sectionName || "");
        });

        groups.push({
          room,
          sections,
          isExpanded: true,
        });
      });

      // Sort groups by room name
      groups.sort((a, b) => a.room.name.localeCompare(b.room.name));

      const avgProcessingTime =
        processingTimes.length > 0
          ? totalProcessingTime / processingTimes.length
          : 0;

      setReportStats({
        totalAnalyses,
        totalDetections,
        totalUserDrawn,
        totalCorrected,
        avgProcessingTime,
        totalSessions: filteredSessions.length,
        confidenceStats: {
          certain: certainDetections,
          uncertain: uncertainDetections,
        },
      });

      setRoomGroups(groups);
    } catch (error) {
      console.error("Failed to fetch report data:", error);
    } finally {
      setLoadingStats(false);
      setLoadingTable(false);
    }
  }, [
    selectedDate,
    selectedRooms,
    sessionsWithDates,
    allRooms,
    user?.id,
    isAdmin,
    sectionCache,
  ]);

  // Fetch testing session report data
  const fetchTestingReportData = useCallback(async () => {
    if (!selectedTestingSession) return;

    try {
      setLoadingStats(true);
      setLoadingTable(true);

      const analysesResponse = await apiService.getTestingSessionAnalyses(selectedTestingSession.id);
      const analyses = analysesResponse.analyses || [];

      let totalAnalyses = analyses.length;
      let totalDetections = 0;
      let totalUserDrawn = 0;
      let totalCorrected = 0;
      let totalProcessingTime = 0;
      let processingTimes: number[] = [];
      let certainDetections = 0;
      let uncertainDetections = 0;

      // Build a single room group for testing session (no sections)
      const detectionsList: DetectionRow[] = [];

      analyses.forEach((analysis: HistoryItem, analysisIndex: number) => {
        totalDetections += analysis.total_detections;
        processingTimes.push(analysis.processing_time);
        totalProcessingTime += analysis.processing_time;

        if (analysis.detections) {
          analysis.detections.forEach((detection: any) => {
            // Check for user correction - either explicit flag or presence of corrected class
            const isUserCorrected = !!(detection.is_user_corrected || detection.user_corrected_class);

            if (detection.user_drawn) {
              totalUserDrawn++;
            }
            if (isUserCorrected && !detection.user_drawn) {
              totalCorrected++;
            }

            if (detection.is_confident) {
              certainDetections++;
            } else {
              uncertainDetections++;
            }

            const topClass =
              detection.user_corrected_class ||
              detection.top_classes?.[0]?.label ||
              detection.original_top1_class ||
              "Unknown";
            const classificationConfidence =
              detection.top_classes?.[0]?.score ||
              detection.confidence ||
              0;

            detectionsList.push({
              id: detection.id,
              sessionId: selectedTestingSession.id,
              analysisId: analysis.id,
              roomName: selectedTestingSession.name,
              sectionId: null,
              sectionName: analysis.image_name,
              imageName: analysis.image_name,
              bbox: detection.bbox,
              topClass,
              confidence: detection.confidence || 0,
              classificationConfidence,
              qualityScore: detection.quality_score,
              isUserCorrected,
              userCorrectedClass: detection.user_corrected_class,
              originalTopClass: detection.original_top1_class,
              isUserDrawn: !!detection.user_drawn,
              timestamp: analysis.timestamp,
              uniqueId: `${analysis.id}-${detection.id}`,
              orderNumber: 0,
              hasUncertainDetections: !detection.is_confident,
            });
          });
        }
      });

      // Sort detections and assign order numbers
      detectionsList.sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );
      detectionsList.forEach((d, i) => (d.orderNumber = i + 1));

      // Group detections by image (using analysisId as key)
      const imageGroups = new Map<number, DetectionRow[]>();
      detectionsList.forEach((detection) => {
        if (!imageGroups.has(detection.analysisId)) {
          imageGroups.set(detection.analysisId, []);
        }
        imageGroups.get(detection.analysisId)!.push(detection);
      });

      // Create sections from image groups
      const sections: SectionGroup[] = [];
      imageGroups.forEach((detections, analysisId) => {
        const firstDetection = detections[0];
        sections.push({
          sectionId: analysisId,
          sectionName: firstDetection?.imageName || `Image ${analysisId}`,
          detections,
          isExpanded: true,
        });
      });

      // Create a single room group for the testing session
      const groups: RoomGroup[] = [{
        room: {
          id: selectedTestingSession.id,
          name: selectedTestingSession.name,
          created_at: selectedTestingSession.created_at,
        } as Room,
        sections,
        isExpanded: true,
      }];

      const avgProcessingTime =
        processingTimes.length > 0
          ? totalProcessingTime / processingTimes.length
          : 0;

      setReportStats({
        totalAnalyses,
        totalDetections,
        totalUserDrawn,
        totalCorrected,
        avgProcessingTime,
        totalSessions: 1,
        confidenceStats: {
          certain: certainDetections,
          uncertain: uncertainDetections,
        },
      });

      setRoomGroups(groups);
    } catch (error) {
      console.error("Failed to fetch testing report data:", error);
    } finally {
      setLoadingStats(false);
      setLoadingTable(false);
    }
  }, [selectedTestingSession]);

  // Effect for fetching testing session data
  useEffect(() => {
    if (reportMode === "testing" && selectedTestingSession) {
      fetchTestingReportData();
    }
  }, [reportMode, selectedTestingSession, fetchTestingReportData]);

  // Reset state when switching modes
  useEffect(() => {
    setReportStats(null);
    setRoomGroups([]);
    if (reportMode === "section-based") {
      setSelectedTestingSession(null);
    } else {
      setSelectedDate(null);
      setSelectedRooms(new Set());
    }
  }, [reportMode]);

  const generateCalendarDays = () => {
    const currentMonthDate = currentMonth.getMonth();
    const currentYear = currentMonth.getFullYear();

    const firstDay = new Date(currentYear, currentMonthDate, 1);
    const lastDay = new Date(currentYear, currentMonthDate + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days: (Date | null)[] = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(currentYear, currentMonthDate, day));
    }

    return days;
  };

  const findMostRecentDateWithSessions = (): Date | null => {
    if (sessionsWithDates.size === 0) return null;

    const availableDates = Array.from(sessionsWithDates.keys())
      .map((dateKey) => new Date(dateKey))
      .filter((date) => isDateClickable(date))
      .sort((a, b) => b.getTime() - a.getTime());

    return availableDates.length > 0 ? availableDates[0] : null;
  };

  const navigateMonth = (direction: "prev" | "next") => {
    const newMonth = new Date(currentMonth);
    if (direction === "prev") {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  const goToMostRecent = () => {
    const mostRecentDate = findMostRecentDateWithSessions();
    if (mostRecentDate) {
      setSelectedDate(mostRecentDate);
      setCurrentMonth(
        new Date(mostRecentDate.getFullYear(), mostRecentDate.getMonth(), 1),
      );
      updateAvailableRooms(mostRecentDate);
    }
  };

  const isDateClickable = (date: Date) => {
    const dateKey = date.toDateString();
    const sessionsOnDate = sessionsWithDates.get(dateKey) || [];

    const completedSessions = sessionsOnDate.filter((session) => {
      const statusMatch = session.status === "completed" || !session.is_active;
      return statusMatch;
    });

    if (isAdmin) {
      return completedSessions.length > 0;
    } else {
      return completedSessions.some((session) => session.user_id === user?.id);
    }
  };

  const updateAvailableRooms = (date: Date) => {
    const dateKey = date.toDateString();
    const sessionsOnDate = sessionsWithDates.get(dateKey) || [];

    const filteredSessions = sessionsOnDate.filter((session) => {
      const userMatch = isAdmin || session.user_id === user?.id;
      const statusMatch = session.status === "completed" || !session.is_active;
      return userMatch && statusMatch;
    });

    const roomIds = new Set(
      filteredSessions.map((session) => session.room_id).filter(Boolean),
    );
    const available = allRooms.filter((room) => roomIds.has(room.id));
    setAvailableRooms(available);

    if (available.length > 0) {
      const mostRecentSession = filteredSessions.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )[0];

      if (mostRecentSession && mostRecentSession.room_id) {
        setSelectedRooms(new Set([mostRecentSession.room_id]));
      } else {
        setSelectedRooms(new Set([available[0].id]));
      }
    }
  };

  const handleDateClick = (date: Date) => {
    if (isDateClickable(date)) {
      urlParamsRoomsApplied.current = false; // User manually changed date, allow normal room selection
      setSelectedDate(date);
      updateAvailableRooms(date);
    }
  };

  const handleRoomToggle = (roomId: number) => {
    if (selectedRooms.has(roomId) && selectedRooms.size <= 1) {
      return;
    }

    const newSelected = new Set(selectedRooms);
    if (newSelected.has(roomId)) {
      newSelected.delete(roomId);
    } else {
      newSelected.add(roomId);
    }
    setSelectedRooms(newSelected);
  };

  const handleSelectAllRooms = () => {
    const allRoomIds = new Set(availableRooms.map((room) => room.id));
    setSelectedRooms(allRoomIds);
  };

  // Flatten detections for export functions
  const getAllDetections = useCallback(() => {
    const detections: DetectionRow[] = [];
    roomGroups.forEach((group) => {
      group.sections.forEach((section) => {
        detections.push(...section.detections);
      });
    });
    return detections;
  }, [roomGroups]);

  // Helper: compute class aggregations for a section
  const getClassAggregations = useCallback((detections: DetectionRow[]) => {
    const classMap = new Map<string, DetectionRow[]>();
    detections.forEach((detection) => {
      const className = detection.topClass;
      if (!classMap.has(className)) {
        classMap.set(className, []);
      }
      classMap.get(className)!.push(detection);
    });

    return Array.from(classMap.entries())
      .map(([className, dets]) => {
        const confidences = dets.map((d) => d.classificationConfidence);
        return {
          className,
          quantity: dets.length,
          detections: dets,
          avgConfidence:
            confidences.reduce((a, b) => a + b, 0) / confidences.length,
          minConfidence: Math.min(...confidences),
          maxConfidence: Math.max(...confidences),
          userCorrectedCount: dets.filter((d) => d.isUserCorrected).length,
          userDrawnCount: dets.filter((d) => d.isUserDrawn).length,
        };
      })
      .sort((a, b) => b.quantity - a.quantity);
  }, []);

  // Excel export - aggregated by class
  const downloadExcelReport = useCallback(() => {
    if (roomGroups.length === 0) return;

    const workbook = XLSX.utils.book_new();

    // Summary sheet - aggregated class + quantity view
    const summaryData: any[] = [];

    roomGroups.forEach((group) => {
      let isFirstRowInRoom = true;

      group.sections.forEach((section) => {
        let isFirstRowInSection = true;
        const aggregations = getClassAggregations(section.detections);

        aggregations.forEach((agg) => {
          const confidenceRange = `${(agg.minConfidence * 100).toFixed(0)}% - ${(agg.maxConfidence * 100).toFixed(0)}%`;

          summaryData.push({
            Room: isFirstRowInRoom ? group.room.name : "",
            Section: isFirstRowInSection
              ? section.sectionName || "Unassigned"
              : "",
            SKU: formatClassNameForDisplay(agg.className),
            Quantity: agg.quantity,
            "Confidence Range": confidenceRange,
            "User Corrected": agg.userCorrectedCount,
            "User Drawn": agg.userDrawnCount,
            "AI Detected": agg.quantity - agg.userDrawnCount,
          });

          isFirstRowInRoom = false;
          isFirstRowInSection = false;
        });
      });
    });

    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    summarySheet["!cols"] = [
      { wch: 20 }, // Room
      { wch: 20 }, // Section
      { wch: 35 }, // SKU
      { wch: 10 }, // Quantity
      { wch: 18 }, // Confidence Range
      { wch: 14 }, // User Corrected
      { wch: 12 }, // User Drawn
      { wch: 12 }, // AI Detected
    ];
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Details sheet - individual detections
    const detailsData: any[] = [];

    roomGroups.forEach((group) => {
      group.sections.forEach((section) => {
        section.detections.forEach((detection) => {
          const confidencePercent = `${(detection.classificationConfidence * 100).toFixed(1)}%`;
          let confidenceIndicator = "";

          if (detection.classificationConfidence >= 0.8) {
            confidenceIndicator = "🟢 ";
          } else if (detection.classificationConfidence >= 0.6) {
            confidenceIndicator = "🟡 ";
          } else {
            confidenceIndicator = "🔴 ";
          }

          detailsData.push({
            Room: group.room.name,
            Section: section.sectionName || "Unassigned",
            Order: detection.orderNumber,
            SKU: formatClassNameForDisplay(detection.topClass),
            Score: confidenceIndicator + confidencePercent,
            "SKU Source": detection.isUserCorrected ? "🔵 Corrected" : "🤖 AI",
            "Detection Source": detection.isUserDrawn ? "👤 Manual" : "🤖 AI",
            Image: detection.imageName,
            Timestamp: new Date(detection.timestamp).toLocaleString(),
          });
        });
      });
    });

    const detailsSheet = XLSX.utils.json_to_sheet(detailsData);
    detailsSheet["!cols"] = [
      { wch: 20 }, // Room
      { wch: 20 }, // Section
      { wch: 8 }, // Order
      { wch: 35 }, // SKU
      { wch: 15 }, // Score
      { wch: 15 }, // SKU Source
      { wch: 15 }, // Detection Source
      { wch: 30 }, // Image
      { wch: 20 }, // Timestamp
    ];
    XLSX.utils.book_append_sheet(workbook, detailsSheet, "Details");

    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}`;
    const filename = `detection-report-${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);
  }, [roomGroups, getClassAggregations]);

  // Helper: convert folder name for lookups
  const toFolderName = (displayName: string): string => {
    return displayName.replace(/ /g, "_");
  };

  // Full PDF export with images and category-colored detections
  const downloadPDFReport = useCallback(async () => {
    if (roomGroups.length === 0 || isGeneratingPDF) return;

    setIsGeneratingPDF(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let currentY = margin;

      const allDetections = getAllDetections();
      const totalDetections = allDetections.length;

      // Helper function to add watermark
      const addWatermark = () => {
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(180, 180, 180);
        const watermarkText =
          "AuthenTrace Systems SA / 2026 / All rights reserved.";
        pdf.text(watermarkText, 8, pageHeight / 2, { angle: 90 });
        pdf.setTextColor(0, 0, 0);
      };

      // Calculate statistics
      let userCorrected = 0;
      let userDrawn = 0;
      let totalConfidence = 0;

      allDetections.forEach((detection) => {
        if (detection.isUserCorrected && !detection.isUserDrawn) {
          userCorrected++;
        } else if (detection.isUserDrawn) {
          userDrawn++;
        }
        totalConfidence += detection.classificationConfidence;
      });

      const avgConfidence =
        totalDetections > 0
          ? ((totalConfidence / totalDetections) * 100).toFixed(0)
          : "0";

      // TITLE PAGE
      addWatermark();

      pdf.setFontSize(28);
      pdf.setFont("times", "bold");
      pdf.setTextColor(31, 41, 55);
      const titleText = "INVENTORY REVIEW";
      const titleWidth = pdf.getTextWidth(titleText);
      pdf.text(titleText, (pageWidth - titleWidth) / 2, currentY);
      currentY += 15;

      pdf.setLineWidth(0.5);
      pdf.setDrawColor(156, 163, 175);
      const lineStart = (pageWidth - titleWidth) / 2;
      const lineEnd = lineStart + titleWidth;
      pdf.line(lineStart, currentY, lineEnd, currentY);
      currentY += 25;

      const now = new Date();
      const timestampReport = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, "0")}/${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

      pdf.setFontSize(16);
      pdf.setFont("times", "normal");
      pdf.setTextColor(75, 85, 99);
      const dateText = `Report Generated: ${timestampReport}`;
      const dateWidth = pdf.getTextWidth(dateText);
      pdf.text(dateText, (pageWidth - dateWidth) / 2, currentY);
      currentY += 30;

      // Executive Summary
      const leftMargin = margin + 10;
      const valueIndent = 75;

      pdf.setFontSize(16);
      pdf.setFont("times", "bold");
      pdf.setTextColor(31, 41, 55);
      pdf.text("EXECUTIVE SUMMARY", leftMargin, currentY);
      currentY += 15;

      pdf.setFontSize(11);
      pdf.setFont("times", "normal");
      pdf.setTextColor(55, 65, 81);

      pdf.text("Total Sessions:", leftMargin, currentY);
      pdf.text(
        `${reportStats?.totalSessions || 0}`,
        leftMargin + valueIndent,
        currentY,
      );
      currentY += 8;

      pdf.text("Total Images:", leftMargin, currentY);
      pdf.text(
        `${reportStats?.totalAnalyses || 0}`,
        leftMargin + valueIndent,
        currentY,
      );
      currentY += 8;

      const detectedClasses = new Set<string>();
      allDetections.forEach((d) => detectedClasses.add(d.topClass));

      pdf.text("Detected SKUs:", leftMargin, currentY);
      pdf.text(`${detectedClasses.size}`, leftMargin + valueIndent, currentY);
      currentY += 8;

      pdf.text("Total Objects:", leftMargin, currentY);
      pdf.text(`${totalDetections}`, leftMargin + valueIndent, currentY);
      currentY += 8;

      pdf.text("Average Confidence:", leftMargin, currentY);
      pdf.text(`${avgConfidence}%`, leftMargin + valueIndent, currentY);
      currentY += 8;

      const accuracyNum =
        totalDetections > 0
          ? ((totalDetections - userCorrected - userDrawn) / totalDetections) *
            100
          : 100;
      pdf.text("System Accuracy:", leftMargin, currentY);
      pdf.setFont("times", "bold");
      if (accuracyNum >= 90) {
        pdf.setTextColor(34, 197, 94);
      } else if (accuracyNum >= 70) {
        pdf.setTextColor(245, 158, 11);
      } else {
        pdf.setTextColor(239, 68, 68);
      }
      pdf.text(
        `${accuracyNum.toFixed(1)}%`,
        leftMargin + valueIndent,
        currentY,
      );
      pdf.setTextColor(55, 65, 81);
      pdf.setFont("times", "normal");

      // Group detections by analysisId, preserve room/section info
      const detectionsByImage = new Map<
        number,
        {
          imageName: string;
          roomName: string;
          sectionName: string | null;
          detections: DetectionRow[];
        }
      >();
      allDetections.forEach((detection) => {
        if (!detectionsByImage.has(detection.analysisId)) {
          detectionsByImage.set(detection.analysisId, {
            imageName: detection.imageName,
            roomName: detection.roomName,
            sectionName: detection.sectionName,
            detections: [],
          });
        }
        detectionsByImage.get(detection.analysisId)!.detections.push(detection);
      });

      // IMAGE PAGES - 1 image per page with detections drawn
      for (const [analysisId, imageData] of detectionsByImage) {
        pdf.addPage();
        addWatermark();
        currentY = margin;

        // Room - Section header at top
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(31, 41, 55);
        const pageHeader = imageData.sectionName
          ? `${imageData.roomName} - ${imageData.sectionName}`
          : imageData.roomName;
        pdf.text(pageHeader, margin, currentY);
        currentY += 12;

        // Load image
        try {
          const imageBlob = await apiService.getAnalysisImageBlob(analysisId);
          const imageUrl = URL.createObjectURL(imageBlob);

          // Create image element to get dimensions
          const img = new Image();
          img.crossOrigin = "anonymous";

          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("Failed to load image"));
            img.src = imageUrl;
          });

          // Calculate image dimensions to fit on page
          const availableWidth = pageWidth - margin * 2;
          const availableHeight = (pageHeight - margin * 2) * 0.55; // 55% of page for image

          const imgAspect = img.naturalWidth / img.naturalHeight;
          const containerAspect = availableWidth / availableHeight;

          let imgWidth, imgHeight;
          if (imgAspect > containerAspect) {
            imgWidth = availableWidth;
            imgHeight = availableWidth / imgAspect;
          } else {
            imgHeight = availableHeight;
            imgWidth = availableHeight * imgAspect;
          }

          const imgX = margin + (availableWidth - imgWidth) / 2;
          const imgY = currentY;

          // Create canvas to draw image with bounding boxes
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d")!;

          // Draw original image
          ctx.drawImage(img, 0, 0);

          // Draw bounding boxes with category colors
          imageData.detections.forEach((detection) => {
            const [x1, y1, x2, y2] = detection.bbox;

            // Try multiple formats for category lookup
            const rawClassId =
              detection.originalTopClass ||
              detection.topClass.replace(/ /g, "_");
            const withSpaces = detection.topClass.replace(/_/g, " ");
            const category =
              getClassCategory(rawClassId) ||
              getClassCategory(detection.topClass) ||
              getClassCategory(withSpaces);
            const color =
              category?.color || getClassColor(rawClassId, category);

            // Draw bounding box
            ctx.strokeStyle = color;
            ctx.lineWidth = Math.max(3, img.naturalWidth / 300);
            ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

            // Draw label background
            const label = formatClassNameForDisplay(detection.topClass);
            const fontSize = Math.max(14, img.naturalWidth / 60);
            ctx.font = `bold ${fontSize}px sans-serif`;
            const labelWidth = ctx.measureText(label).width + 10;
            const labelHeight = fontSize + 8;

            ctx.fillStyle = color;
            ctx.fillRect(x1, y1 - labelHeight, labelWidth, labelHeight);

            // Draw label text
            ctx.fillStyle = "white";
            ctx.fillText(label, x1 + 5, y1 - 5);
          });

          // Convert canvas to data URL with compression (0.7 quality for smaller file)
          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);

          // Add image to PDF
          pdf.addImage(dataUrl, "JPEG", imgX, imgY, imgWidth, imgHeight);

          // Clean up
          URL.revokeObjectURL(imageUrl);

          // Add class breakdown below image
          currentY = imgY + imgHeight + 8;

          // Group detections by class with full detection info
          const classGroups = new Map<
            string,
            { displayName: string; detections: typeof imageData.detections }
          >();
          imageData.detections.forEach((d) => {
            const displayName = formatClassNameForDisplay(d.topClass);
            if (!classGroups.has(displayName)) {
              classGroups.set(displayName, { displayName, detections: [] });
            }
            classGroups.get(displayName)!.detections.push(d);
          });

          // Sort by count (descending)
          const sortedClassGroups = Array.from(classGroups.entries()).sort(
            (a, b) => b[1].detections.length - a[1].detections.length,
          );

          // Table configuration
          const tableStartX = margin + 5;
          const colNumWidth = isAdmin ? 8 : 12;
          const colDetWidth = 15;
          const colRefWidth = 15;
          const colClsWidth = 15;
          const colSkuSrcWidth = 12;
          const colDetSrcWidth = 12;
          const rowHeight = 4;

          sortedClassGroups.forEach(([, group]) => {
            if (currentY > pageHeight - margin - 20) {
              return; // Skip if we run out of space
            }

            // Class name header with quantity
            pdf.setFontSize(9);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(31, 41, 55);
            pdf.text(
              `${group.displayName} (${group.detections.length})`,
              tableStartX,
              currentY,
            );
            currentY += 5;

            // Table header row (admin only)
            if (isAdmin) {
              pdf.setFontSize(7);
              pdf.setFont("helvetica", "bold");
              pdf.setTextColor(100, 100, 100);
              let headerX = tableStartX;
              pdf.text("#", headerX, currentY);
              headerX += colNumWidth;
              pdf.text("Det", headerX, currentY);
              headerX += colDetWidth;
              pdf.text("Ref", headerX, currentY);
              headerX += colRefWidth;
              pdf.text("Cls", headerX, currentY);
              headerX += colClsWidth;
              pdf.text("SKU Src", headerX, currentY);
              headerX += colSkuSrcWidth;
              pdf.text("Det Src", headerX, currentY);
              currentY += rowHeight;
            }

            // Individual detections
            group.detections.forEach((det, idx) => {
              if (currentY > pageHeight - margin - 10) {
                return; // Skip if we run out of space
              }

              pdf.setFontSize(7);
              pdf.setFont("helvetica", "normal");

              // Color based on detection source
              // Green: AI detected and properly classified
              // Purple: User corrected class
              // Blue: User drawn detection
              if (det.isUserDrawn) {
                pdf.setTextColor(59, 130, 246); // Blue
              } else if (det.isUserCorrected) {
                pdf.setTextColor(147, 51, 234); // Purple
              } else {
                pdf.setTextColor(34, 197, 94); // Green
              }

              let cellX = tableStartX;
              pdf.text(`#${idx + 1}`, cellX, currentY);

              if (isAdmin) {
                cellX += colNumWidth;
                // Detection score (confidence)
                const detScore = det.confidence != null ? (det.confidence * 100).toFixed(2) : "-";
                pdf.text(detScore, cellX, currentY);

                cellX += colDetWidth;
                // Refinement score (quality_score)
                const refScore = det.qualityScore != null ? (det.qualityScore * 100).toFixed(2) : "-";
                pdf.text(refScore, cellX, currentY);

                cellX += colRefWidth;
                // Classification score
                const clsScore = det.classificationConfidence != null ? (det.classificationConfidence * 100).toFixed(2) : "-";
                pdf.text(clsScore, cellX, currentY);

                cellX += colClsWidth;
                // SKU Source (AI or User corrected)
                const skuSrc = det.isUserCorrected ? "User" : "AI";
                pdf.text(skuSrc, cellX, currentY);

                cellX += colSkuSrcWidth;
                // Detection Source (AI or User drawn)
                const detSrc = det.isUserDrawn ? "User" : "AI";
                pdf.text(detSrc, cellX, currentY);
              }

              currentY += rowHeight;
            });

            currentY += 2; // Space between class groups
          });

          // Total for this image
          currentY += 3;
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(31, 41, 55);
          pdf.text(
            `Total: ${imageData.detections.length} objects`,
            margin + 5,
            currentY,
          );
        } catch (error) {
          console.error(`Failed to load image ${analysisId}:`, error);
          // Add error placeholder
          pdf.setFontSize(12);
          pdf.setTextColor(200, 50, 50);
          pdf.text(
            `Failed to load image: ${imageData.roomName}`,
            margin,
            currentY + 50,
          );
        }
      }

      // Save PDF
      const timestamp2 = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      pdf.save(`inventory-report-${timestamp2}.pdf`);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [
    roomGroups,
    reportStats,
    isGeneratingPDF,
    getAllDetections,
    getClassCategory,
    isAdmin,
  ]);

  const calendarDays = generateCalendarDays();
  const monthNames = t("reports.calendar_months", {
    returnObjects: true,
  }) as string[];
  const dayNames = t("reports.calendar_days", {
    returnObjects: true,
  }) as string[];

  const totalDetections = useMemo(
    () =>
      roomGroups.reduce(
        (sum, group) =>
          sum +
          group.sections.reduce(
            (sSum, section) => sSum + section.detections.length,
            0,
          ),
        0,
      ),
    [roomGroups],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-serif font-bold text-gray-900">
              {t("reports.title")}
            </h1>
            <p className="text-sm text-gray-500">{t("reports.subtitle")}</p>
          </div>

          {/* Admin-only mode toggle */}
          {isAdmin && (
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setReportMode("section-based")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                  reportMode === "section-based"
                    ? "bg-white text-primary-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                <FolderOpen className="h-4 w-4" />
                Sessions
              </button>
              <button
                onClick={() => setReportMode("testing")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                  reportMode === "testing"
                    ? "bg-white text-purple-700 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                <FlaskConical className="h-4 w-4" />
                Testing
              </button>
            </div>
          )}
        </div>

        {/* Export buttons */}
        {roomGroups.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPDFReport}
              disabled={isGeneratingPDF}
              className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {isGeneratingPDF ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              PDF
            </button>
            <button
              onClick={downloadExcelReport}
              className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
            >
              <Table className="h-4 w-4" />
              Excel
            </button>
          </div>
        )}
      </div>

      {/* Filters + Stats Row - Stack on mobile */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-start">
        {/* Section-based mode: Calendar + Room filters */}
        {reportMode === "section-based" && (
          <ReportFilters
            selectedDate={selectedDate}
            currentMonth={currentMonth}
            onDateClick={handleDateClick}
            onMonthNavigate={navigateMonth}
            onGoToMostRecent={goToMostRecent}
            isDateClickable={isDateClickable}
            calendarDays={calendarDays}
            monthNames={monthNames}
            dayNames={dayNames}
            availableRooms={availableRooms}
            selectedRooms={selectedRooms}
            onRoomToggle={handleRoomToggle}
            onSelectAllRooms={handleSelectAllRooms}
          />
        )}

        {/* Testing mode: Session selector */}
        {reportMode === "testing" && (
          <div className="bg-white rounded-xl shadow-sm border p-4 w-full lg:w-80 flex-shrink-0">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-purple-600" />
              Testing Sessions
            </h3>
            {testingSessions.length === 0 ? (
              <p className="text-sm text-gray-500">No testing sessions available</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {testingSessions.map((session) => (
                  <button
                    key={session.id}
                    onClick={() => setSelectedTestingSession(session)}
                    className={cn(
                      "w-full text-left p-3 rounded-lg border transition-colors",
                      selectedTestingSession?.id === session.id
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    <div className="font-medium text-gray-900 text-sm truncate">
                      {session.name}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span>{session.image_count || 0} images</span>
                      <span>•</span>
                      <span>
                        {new Date(session.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stats Bar - Expands to fill remaining space */}
        <div className="flex-1">
          <ReportStatsBar
            stats={reportStats}
            loading={loadingStats}
            selectedDate={reportMode === "section-based" ? selectedDate : (selectedTestingSession ? new Date(selectedTestingSession.created_at) : null)}
            selectedRoomsCount={reportMode === "section-based" ? selectedRooms.size : (selectedTestingSession ? 1 : 0)}
          />

          {/* Empty state when no date/room/session selected */}
          {reportMode === "section-based" && (!selectedDate || selectedRooms.size === 0) && (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center mt-4">
              <Target className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-700 mb-1">
                {!selectedDate ? "Select a Date" : "Select Rooms"}
              </h3>
              <p className="text-sm text-gray-500">
                {!selectedDate
                  ? "Pick a date from the calendar to view reports"
                  : `Choose rooms to view their reports for ${selectedDate.toLocaleDateString()}`}
              </p>
            </div>
          )}

          {reportMode === "testing" && !selectedTestingSession && (
            <div className="bg-white rounded-xl shadow-sm border p-8 text-center mt-4">
              <FlaskConical className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-700 mb-1">
                Select a Testing Session
              </h3>
              <p className="text-sm text-gray-500">
                Choose a testing session from the list to view its report
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Detections Table */}
      {((reportMode === "section-based" && selectedDate && selectedRooms.size > 0) ||
        (reportMode === "testing" && selectedTestingSession)) && (
        <DetectionsTable
          roomGroups={roomGroups}
          onRoomGroupsChange={setRoomGroups}
          loading={loadingTable}
          showDetails={showDetails}
          onShowDetailsChange={setShowDetails}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

export default ReportsPage;
